import React from 'react';

import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  DataReference,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import isArchived from 'archive/isArchived';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import {
  GetDataFrameColumns,
  GetDataFrameColumnsVariables,
} from 'data-frames/graphql/__generated/GetDataFrameColumns';
import {GET_DATA_FRAME_COLUMNS_QUERY} from 'data-frames/graphql/GetDataFrame';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ResultsTableAdapter from 'notebook/integrations/results/ResultsTableAdapter';
import {ResultsTablePreview} from 'notebook/integrations/results/ResultsTablePreview';
import useSelectorsById, {selectorsAreLoaded} from 'notebook/structured-tables/hooks/useSelectorsById';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import QueryingResultSchemaSelector from 'results/ResultSchemaSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

import {updateResultSchema} from './DataMappingStateReducers';
import getDataMappingConfirmationButtons from './getDataMappingConfirmationButtons';
import useDataMappingState from './useDataMappingState';

export interface DataMappingModalProps {
  analysisId: string;
  inputDataReference: DataReference;
  dataFrameId: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
}

function InternalDataMappingModal(
  props: DataMappingModalProps & {dataFrameColumns: DataFrameColumns}
): JSX.Element {
  const [dataMappingState, setDataMappingState, resetDataMappingState] = useDataMappingState(
    props.analysisId,
    props.dataFrameId
  );
  const {resultSchema} = dataMappingState;

  const onClose = (): void => {
    resetDataMappingState();
    props.onRequestClose();
  };

  const onNext = (): void => {
    props.onOpenStepInitializationView({
      type: AnalysisMainViewItemType.SAVE_TO_RESULTS_CONFIGURATION,
      inputDataReference: props.inputDataReference,
      inputDataFrameId: props.dataFrameId,
    });
    props.onRequestClose();
  };

  const buttons = getDataMappingConfirmationButtons({resultSchema, onClose, onNext});

  const selectorsById = useSelectorsById({includeArchivedSelectors: true});

  return (
    <Modal title="Save to Results" buttons={buttons} onRequestClose={props.onRequestClose}>
      <FormGroup>
        <FieldLabel label="Result schema" />
        <QueryingResultSchemaSelector
          selectedSchema={resultSchema}
          onChange={(newResultSchema) => {
            setDataMappingState(
              updateResultSchema(dataMappingState, newResultSchema, props.dataFrameColumns)
            );
          }}
          filter={(resultSchema) => !isArchived(resultSchema)}
        />
        {resultSchema && selectorsAreLoaded(selectorsById) && (
          <div className="mediocre">
            <ResultsTablePreview
              schemaAndColumnGroup={{...resultSchema, columnGroup: null}}
              adapter={new ResultsTableAdapter(StructuredTableKind.RESULTS)}
              selectorsById={selectorsById}
            />
          </div>
        )}
      </FormGroup>
    </Modal>
  );
}

export default function DataMappingModal(props: DataMappingModalProps): JSX.Element {
  return (
    <BenchlingQuery<GetDataFrameColumns, GetDataFrameColumnsVariables>
      query={GET_DATA_FRAME_COLUMNS_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId: props.dataFrameId}}
    >
      {({dataFrame: dataFrameNode}) =>
        isInaccessible(dataFrameNode) ? null : (
          <InternalDataMappingModal {...props} dataFrameColumns={dataFrameNode.allColumnsJson} />
        )
      }
    </BenchlingQuery>
  );
}
